import React, { useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Prompt } from "react-router-dom";

import Input from "../../../../../components/Form/Input";
import ActionButtons from "../../../../../components/Generic/ActionButtons";
import Skeleton from "../../../../../components/Generic/Skeleton";
import { useAppState } from "../../../../../context/StateContext";
import useTimezoneDate from "../../../../../hooks/useTimezoneDate";
import { Financials } from "../../../../../models/financials";
import useFinancialActions from "./actions";
import useStyles from "./styles";

const FinancialsComponent = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { hasUnsavedChanges, setHasUnsavedChanges } = useAppState();

  const { useGetFinancials, useUpdateFinancials } = useFinancialActions();
  const { getDateValue, getSaveDateValue } = useTimezoneDate();

  const [financials, setFinancials] = useState<Financials>({});

  const { isFetching: loadingFinancials } = useGetFinancials(setFinancials);
  const { mutate: updateFinancials, isLoading: updatingFinancials } = useUpdateFinancials(() =>
    setHasUnsavedChanges(false)
  );

  const PLACEHOLDER = "123545341";

  const handleSetHasUnsavedChanges = (value: boolean) => {
    if (value && !hasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }

    if (!value && hasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };

  const handleSave = () => {
    updateFinancials(financials);
  };

  return (
    <Grid className={classes.generalWrapper}>
      <Grid padding="8px">
        <span className={classes.title}>{translate("financials")}</span>
      </Grid>

      <Grid
        item
        margin="24px 0px"
        direction="column"
        borderRadius="12px"
        border="1px solid #E4E7EC"
        padding="24px"
        spacing={2}
      >
        <Prompt when={hasUnsavedChanges} message="" />
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <span className={classes.subtitle}>{translate("beneficiaryDetails")}</span>
          </Grid>
          <Grid container item direction="row" xs={12}>
            <Grid item xs={8}>
              <Skeleton isLoading={loadingFinancials} isInput>
                <Input
                  name="beneficiary-bank"
                  label={translate("beneficiaryBank")}
                  placeholder={PLACEHOLDER}
                  value={financials?.beneficiaryBank}
                  onChange={(event) => {
                    handleSetHasUnsavedChanges(true);
                    setFinancials({ ...financials, beneficiaryBank: event.target.value });
                  }}
                />
              </Skeleton>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Skeleton isLoading={loadingFinancials} isInput>
              <Input
                name="account-number"
                label={translate("accountNumber")}
                placeholder={PLACEHOLDER}
                value={financials?.accountNumber}
                onChange={(event) => {
                  handleSetHasUnsavedChanges(true);
                  setFinancials({ ...financials, accountNumber: event.target.value });
                }}
              />
            </Skeleton>
          </Grid>
          <Grid item xs={6}>
            <Skeleton isLoading={loadingFinancials} isInput>
              <Input
                name="ci-date"
                data-cy="ci-date"
                type="date"
                label={translate("ciDate")}
                value={getDateValue(financials?.ciDate)}
                onChange={(event) => {
                  handleSetHasUnsavedChanges(true);
                  setFinancials({ ...financials, ciDate: getSaveDateValue(event.target.value) });
                }}
              />
            </Skeleton>
          </Grid>
          <Grid container item xs={12} justifyContent="flex-end">
            <ActionButtons loading={updatingFinancials} onSave={handleSave} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default FinancialsComponent;
